import type {
    VoteType,
} from "@mrflark/spl_sdk";
import type {
    Filters,
    MediaItem,
    QueryInterpretation,
    SearchMode,
} from "@mrflark/spl_service_model";
import {
    create,
} from "zustand";
import {
    persist,
} from "zustand/middleware";
import type {
    BrowsingMode,
} from "../constants.ts";

export type LeftTab = "browse" | "search";

type ViewerState = {
    currentItem: MediaItem | null;
    isLoading: boolean;
    error: string | null;
    activeLeftTab: LeftTab;
    browsingMode: BrowsingMode;
    rightSidebarOpen: boolean;
    filmstripVisible: boolean;
    orderedItems: MediaItem[];
    orderedIndex: number;
    orderedTotalCount: number;
    orderedHasMore: boolean;
    prefetchedItem: MediaItem | null;
    filters: Filters;
    totalFound: number;
    exactCount: number | null;
    isLoadingExactCount: boolean;
    recentlyViewedIds: string[];
    currentUserVote: VoteType | null;
    recentItems: MediaItem[];
    viewHistory: string[];
    historyIndex: number;
    searchQuery: string;
    searchMode: SearchMode;
    searchResults: MediaItem[];
    searchResultsIndex: number;
    searchTotalCount: number;
    searchHasMore: boolean;
    isSearchActive: boolean;
    isSearching: boolean;
    queryInterpretation: QueryInterpretation | null;
    setCurrentItem: (item: MediaItem | null) => void;
    setIsLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setBrowsingMode: (mode: BrowsingMode) => void;
    setActiveLeftTab: (tab: LeftTab) => void;
    setRightSidebarOpen: (open: boolean) => void;
    setFilmstripVisible: (visible: boolean) => void;
    setOrderedItems: (
        items: MediaItem[],
        totalCount: number,
        hasMore: boolean,
    ) => void;
    appendOrderedItems: (items: MediaItem[], hasMore: boolean) => void;
    setOrderedIndex: (index: number) => void;
    setPrefetchedItem: (item: MediaItem | null) => void;
    setFilters: (filters: Partial<Filters>) => void;
    clearFilters: () => void;
    setTotalFound: (count: number) => void;
    setExactCount: (count: number | null) => void;
    setIsLoadingExactCount: (loading: boolean) => void;
    setCurrentUserVote: (vote: VoteType | null) => void;
    addRecentlyViewed: (id: string, item?: MediaItem) => void;
    clearRecentlyViewed: () => void;
    updateCurrentItemTags: (tags: string[]) => void;
    updateCurrentItemVotes: (votes: number) => void;
    updateCurrentItemVoteCounts: (
        likeCount: number,
        dislikeCount: number,
        loveCount: number,
    ) => void;
    addToHistory: (id: string) => void;
    canGoBack: () => boolean;
    goBack: () => string | null;
    resetOrderedState: () => void;
    setSearchQuery: (query: string) => void;
    setSearchMode: (mode: SearchMode) => void;
    setSearchResults: (
        items: MediaItem[],
        totalCount: number,
        hasMore: boolean,
        interpretation: QueryInterpretation | null,
    ) => void;
    appendSearchResults: (items: MediaItem[], hasMore: boolean) => void;
    setSearchResultsIndex: (index: number) => void;
    setIsSearching: (searching: boolean) => void;
    activateSearch: () => void;
    clearSearch: () => void;
};

const MAX_RECENTLY_VIEWED = 100;
const MAX_RECENT_ITEMS = 20;
const MAX_HISTORY = 100;

const initialFilters: Filters = {
    tag_query: "",
    tags: [],
    tag_mode: "or",
    min_views: undefined,
    max_views: undefined,
    min_votes: undefined,
    max_votes: undefined,
    search_text: "",
    media_type: "all",
    has_watermark: undefined,
    min_ai_confidence: undefined,
};

const updateCurrentItemField = (
    state: ViewerState,
    updatedFields: Partial<MediaItem>,
): Partial<ViewerState> => {
    const currentItem = state.currentItem;
    if (!currentItem) return {};

    const itemId = currentItem.id;

    const updateInArray = (items: MediaItem[]) =>
        items.map((item) => (item.id === itemId
            ? {
                ...item,
                ...updatedFields,
            }
            : item)
        );

    return {
        currentItem: {
            ...currentItem,
            ...updatedFields,
        },
        orderedItems: updateInArray(state.orderedItems),
        searchResults: updateInArray(state.searchResults),
        prefetchedItem: state.prefetchedItem?.id === itemId
            ? {
                ...state.prefetchedItem,
                ...updatedFields,
            }
            : state.prefetchedItem,
    };
};

export const useViewerStore = create<ViewerState>()(
    persist(
        (set, get) => ({
            currentItem: null,
            isLoading: false,
            error: null,
            activeLeftTab: "browse" as LeftTab,
            browsingMode: "random" as BrowsingMode,
            rightSidebarOpen: true,
            filmstripVisible: false,
            orderedItems: [],
            orderedIndex: -1,
            orderedTotalCount: 0,
            orderedHasMore: false,
            prefetchedItem: null,
            filters: initialFilters,
            totalFound: 0,
            exactCount: null,
            isLoadingExactCount: false,
            currentUserVote: null,
            recentlyViewedIds: [],
            recentItems: [],
            viewHistory: [],
            historyIndex: -1,
            searchQuery: "",
            searchMode: "unified" as SearchMode,
            searchResults: [],
            searchResultsIndex: -1,
            searchTotalCount: 0,
            searchHasMore: false,
            isSearchActive: false,
            isSearching: false,
            queryInterpretation: null,

            setCurrentItem: (item) =>
                set({
                    currentItem: item,
                    error: null,
                    currentUserVote: null,
                }),

            setIsLoading: (loading) =>
                set({
                    isLoading: loading,
                }),

            setError: (error) =>
                set({
                    error,
                    isLoading: false,
                }),

            setBrowsingMode: (mode) =>
                set((state) => {
                    if (mode !== state.browsingMode) {
                        return {
                            browsingMode: mode,
                            orderedItems: [],
                            orderedIndex: -1,
                            orderedTotalCount: 0,
                            orderedHasMore: false,
                            prefetchedItem: null,
                            exactCount: null,
                        };
                    }
                    return {
                        browsingMode: mode,
                    };
                }),

            setActiveLeftTab: (tab) =>
                set({
                    activeLeftTab: tab,
                }),

            setRightSidebarOpen: (open) =>
                set({
                    rightSidebarOpen: open,
                }),

            setFilmstripVisible: (visible) =>
                set({
                    filmstripVisible: visible,
                }),

            setOrderedItems: (items, totalCount, hasMore) =>
                set({
                    orderedItems: items,
                    orderedTotalCount: totalCount,
                    orderedHasMore: hasMore,
                    orderedIndex: items.length > 0 ? 0 : -1,
                }),

            appendOrderedItems: (items, hasMore) =>
                set((state) => ({
                    orderedItems: [...state.orderedItems, ...items],
                    orderedHasMore: hasMore,
                })),

            setOrderedIndex: (index) =>
                set({
                    orderedIndex: index,
                }),

            setPrefetchedItem: (item) =>
                set({
                    prefetchedItem: item,
                }),

            setFilters: (newFilters) =>
                set((state) => ({
                    filters: {
                        ...state.filters,
                        ...newFilters,
                    },
                    exactCount: null,
                    orderedItems: [],
                    orderedIndex: -1,
                    orderedTotalCount: 0,
                    orderedHasMore: false,
                    prefetchedItem: null,
                })),

            clearFilters: () =>
                set({
                    filters: initialFilters,
                    exactCount: null,
                    orderedItems: [],
                    orderedIndex: -1,
                    orderedTotalCount: 0,
                    orderedHasMore: false,
                    prefetchedItem: null,
                }),

            resetOrderedState: () =>
                set({
                    orderedItems: [],
                    orderedIndex: -1,
                    orderedTotalCount: 0,
                    orderedHasMore: false,
                    prefetchedItem: null,
                }),

            setTotalFound: (count) =>
                set({
                    totalFound: count,
                }),

            setExactCount: (count) =>
                set({
                    exactCount: count,
                }),

            setIsLoadingExactCount: (loading) =>
                set({
                    isLoadingExactCount: loading,
                }),

            addRecentlyViewed: (id, item) =>
                set((state) => {
                    const ids = [
                        id,
                        ...state.recentlyViewedIds.filter((existing) =>
                            existing !== id
                        ),
                    ];
                    const updatedRecentItems = item
                        ? [
                            item,
                            ...state.recentItems.filter((existing) =>
                                existing.id !== id
                            ),
                        ].slice(0, MAX_RECENT_ITEMS)
                        : state.recentItems;
                    return {
                        recentlyViewedIds: ids.slice(0, MAX_RECENTLY_VIEWED),
                        recentItems: updatedRecentItems,
                    };
                }),

            clearRecentlyViewed: () =>
                set({
                    recentlyViewedIds: [],
                    recentItems: [],
                }),

            updateCurrentItemTags: (tags) =>
                set((state) =>
                    updateCurrentItemField(state, {
                        tags,
                    })
                ),

            updateCurrentItemVotes: (votes) =>
                set((state) =>
                    updateCurrentItemField(state, {
                        votes,
                    })
                ),

            setCurrentUserVote: (vote) =>
                set({
                    currentUserVote: vote,
                }),

            updateCurrentItemVoteCounts: (
                likeCount,
                _dislikeCount,
                loveCount,
            ) => set((state) =>
                updateCurrentItemField(state, {
                    votes: likeCount + loveCount,
                })
            ),

            addToHistory: (id) =>
                set((state) => {
                    const newHistory = state.viewHistory.slice(
                        0,
                        state.historyIndex + 1,
                    );
                    newHistory.push(id);
                    const trimmedHistory = newHistory.slice(-MAX_HISTORY);
                    return {
                        viewHistory: trimmedHistory,
                        historyIndex: trimmedHistory.length - 1,
                    };
                }),

            canGoBack: () => {
                const state = get();
                return state.historyIndex > 0;
            },

            goBack: () => {
                const state = get();
                if (state.historyIndex > 0) {
                    const newIndex = state.historyIndex - 1;
                    const previousId = state.viewHistory[newIndex];
                    set({
                        historyIndex: newIndex,
                    });
                    return previousId;
                }
                return null;
            },

            setSearchQuery: (query) =>
                set({
                    searchQuery: query,
                }),

            setSearchMode: (mode) =>
                set({
                    searchMode: mode,
                }),

            setSearchResults: (items, totalCount, hasMore, interpretation) =>
                set({
                    searchResults: items,
                    searchTotalCount: totalCount,
                    searchHasMore: hasMore,
                    searchResultsIndex: items.length > 0 ? 0 : -1,
                    queryInterpretation: interpretation,
                    isSearchActive: true,
                }),

            appendSearchResults: (items, hasMore) =>
                set((state) => ({
                    searchResults: [...state.searchResults, ...items],
                    searchHasMore: hasMore,
                })),

            setSearchResultsIndex: (index) =>
                set({
                    searchResultsIndex: index,
                }),

            setIsSearching: (searching) =>
                set({
                    isSearching: searching,
                }),

            activateSearch: () =>
                set({
                    isSearchActive: true,
                }),

            clearSearch: () =>
                set({
                    searchQuery: "",
                    searchResults: [],
                    searchResultsIndex: -1,
                    searchTotalCount: 0,
                    searchHasMore: false,
                    isSearchActive: false,
                    isSearching: false,
                    queryInterpretation: null,
                }),
        }),
        {
            name: "viewer-storage",
            partialize: (state) => ({
                activeLeftTab: state.activeLeftTab,
                browsingMode: state.browsingMode,
                rightSidebarOpen: state.rightSidebarOpen,
                filmstripVisible: state.filmstripVisible,
            }),
            merge: (persisted, current) => {
                const restored = persisted as
                    | Record<string, unknown>
                    | undefined;
                const tab = restored?.activeLeftTab as string | undefined;
                return {
                    ...current,
                    ...restored,
                    activeLeftTab: (tab === "browse" || tab === "search"
                        ? tab
                        : "browse") as LeftTab,
                };
            },
        },
    ),
);
